// NewLLMBanner — green alert at the bottom of "Your Silos" listing any
// LLMs released by Google/OpenAI/Anthropic in the last 60 days that the
// creator hasn't dismissed. Backed by /me/new-llms.
//
// May 25, 2026. Empty when the registry is empty (the agent updates the
// backend list when a new model lands). Each entry shows a model name,
// a FREE pill (when applicable), a one-line "how to switch" tip, and a
// "Dismiss" button so it doesn't keep nagging.

import { useEffect, useState } from "react";
import { Sparkle, X, Lightning } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function NewLLMBanner() {
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/me/new-llms");
      setItems(r.data?.new_llms || []);
    } catch {
      // Silent — banner is non-critical.
    }
  };

  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    try {
      await api.post(`/me/new-llms/${encodeURIComponent(id)}/dismiss`);
      setItems((cur) => cur.filter((x) => x.id !== id));
    } catch {
      // ignore — UI will rehydrate on next page load
    }
  };

  if (!items.length) return null;

  return (
    <div className="mt-8 space-y-2" data-testid="new-llm-banners">
      {items.map((it) => (
        <div
          key={it.id}
          className="bg-[#E6F2E6] border border-[#2F6B3A] rounded-md p-4 flex items-start gap-3"
          data-testid={`new-llm-banner-${it.id}`}
        >
          <div className="w-9 h-9 rounded-md bg-white/60 text-[#2F6B3A] flex items-center justify-center shrink-0">
            {it.free_tier ? <Lightning size={18} weight="duotone" /> : <Sparkle size={18} weight="duotone" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#2F6B3A]">
                New LLM detected
              </span>
              {it.free_tier && (
                <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-[#2F6B3A] text-white font-medium">
                  FREE TIER
                </span>
              )}
            </div>
            <div className="text-sm font-medium text-[#1C1B1A] mt-1">{it.label}</div>
            {it.notes && (
              <p className="text-xs text-[#2F6B3A] mt-1 leading-relaxed">{it.notes}</p>
            )}
            <p className="text-[11px] text-[#4A5D4E] mt-2">
              <strong>To switch:</strong> open any silo → Operations → tap "Switch to {it.label.split(" ").slice(-1)[0]}" in the header card,
              OR open the LLM Routing card under Operations to apply across all your silos.
            </p>
          </div>
          <button
            type="button"
            onClick={() => dismiss(it.id)}
            data-testid={`new-llm-dismiss-${it.id}`}
            className="text-[#4A5D4E] hover:text-[#1C1B1A] p-1 shrink-0"
            title="Dismiss this notice"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
